import type { FC } from "react";
import { useMemo } from "react";

import { SearchHeaderButton } from "@ui/components/Search";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { DeviceDefaultIcon } from "@jti/brand-icons/device-default";
import { StoreLocatorIcon } from "@jti/brand-icons/store-locator";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { type AccountDropdownSettings } from "../../Header";
import { UserActions } from "../UserActions/UserActions";

import styles from "./Tools.module.scss";

export type ToolsProps = {
  accountLink?: string;
  accountLabel?: string;
  accountLoadingLabel?: string;
  accountDropdownSettings?: AccountDropdownSettings;
  storeLocatorLink?: string;
  storeLocatorLabel?: string;
  openMenuLabel?: string;
  closeMenuLabel?: string;
  navAriaLabel?: string;
  languageLabel?: string;
  searchLabel?: string;
  mobileAppLink?: string;
  resultsLink?: string;
  onOpenSearch: () => void;
  onCloseSearch: () => void;
  isSearchOpened: boolean;
  renderMiniCart?: () => JSX.Element;
  withCart?: boolean;
  welcomeText?: string;
  onLogOut: () => void;
  isEnabledUserIndication?: boolean;
  isEnabledUserNameForIndication?: boolean;
  tabletUserNameRef?: React.RefObject<HTMLDivElement | null>;
  currentPathname?: string;
};

export const Tools: FC<ToolsProps> = ({
  accountLink,
  accountLabel,
  accountLoadingLabel,
  accountDropdownSettings,
  mobileAppLink,
  storeLocatorLink,
  storeLocatorLabel,
  resultsLink,
  isSearchOpened,
  renderMiniCart,
  withCart,
  onCloseSearch,
  onOpenSearch,
  onLogOut,
  tabletUserNameRef,
  isEnabledUserIndication,
  isEnabledUserNameForIndication,
  currentPathname: currentPathnameProp,
}) => {
  const config = useConfig();
  const { __ } = useTranslations();
  const currentPathname = useMemo(
    () => currentPathnameProp ?? (typeof window !== "undefined" ? window.location.pathname : ""),
    [currentPathnameProp]
  );
  return (
    <div className={styles.tools}>
      <ul className={styles.list}>
        {resultsLink && (
          <li>
            <SearchHeaderButton isOpened={isSearchOpened} onClose={onCloseSearch} onOpen={onOpenSearch} />
          </li>
        )}

        {accountLink && (
          <li className={styles.userButtonContainer}>
            <UserActions
              accountLink={accountLink}
              accountLabel={accountLabel}
              accountLoadingLabel={accountLoadingLabel}
              accountDropdownSettings={accountDropdownSettings}
              onLogOut={onLogOut}
              isEnabledUserIndication={isEnabledUserIndication}
              isEnabledUserNameForIndication={isEnabledUserNameForIndication}
              tabletUserNameRef={tabletUserNameRef}
            />
          </li>
        )}

        {withCart && renderMiniCart && (
          <li>
            <div
              data-testid="miniCart"
              className={classNames(styles.item, styles.miniCartItem, {
                [styles.isActive]: currentPathname.includes(config.CART_CHECKOUT_PAGE_URL),
              })}
              data-cart-mini-container
            >
              {renderMiniCart()}
            </div>
          </li>
        )}

        {storeLocatorLink && (
          <li>
            <LinkBehaviour
              aria-label={storeLocatorLabel}
              title={storeLocatorLabel}
              href={storeLocatorLink}
              className={classNames(styles.item, {
                [styles.isActive]: currentPathname.includes(storeLocatorLink),
              })}
              data-testid="headerStoreLocatorButton"
              onClick={() => dispatchTrackingEvent(TrackingEvents.TrackingStoreLocatorClick)}
            >
              <StoreLocatorIcon className={styles.icon} />
            </LinkBehaviour>
          </li>
        )}

        {mobileAppLink && (
          <li>
            <LinkBehaviour
              href={mobileAppLink}
              className={classNames(styles.item, { [styles.isActive]: currentPathname.includes(mobileAppLink) })}
              data-testid="mobileApp"
              aria-label={__("headerlinks.button.mobileapp")}
            >
              <DeviceDefaultIcon className={styles.icon} />
            </LinkBehaviour>
          </li>
        )}
      </ul>
    </div>
  );
};
